import { z } from 'zod';

import {
  VabsAchAccountType,
  VabsParticipantType,
  VabsPaymentSourceOfFunds,
  VabsPaymentSystem,
  VabsWireAccountType,
} from '@/shared/api';
import { ICurrencyAccount } from '@/shared/lib';

import {
  BANK_ACCOUNT_NO_REGEX,
  BANK_CODE_REGEX,
  BANK_NAME_REGEX,
  BENEFICIARY_ADDRESS_REGEX,
  COMPANY_NAME_REGEX,
  FIRST_NAME_REGEX,
} from '../config';

function isValidBankCode(code: string): boolean {
  if (!code) return false;
  const trimmed = code.trim();

  return trimmed.length === 8 || trimmed.length === 11;
}

const bankAddressSchema = z.object({
  country: z.object(
    { id: z.string({ message: 'Field is required' }), name: z.string() },
    { message: 'Field is required' },
  ),
  street: z.string({ message: 'Field is required' }).min(1, 'Field is required').regex(BENEFICIARY_ADDRESS_REGEX, 'Invalid format'),
  postcode: z.string({ message: 'Field is required' }).min(1, 'Field is required'),
  city: z.string({ message: 'Field is required' }).min(1, 'Field is required'),
});

const bankInfoSchema = z.object({
  name: z
    .string({ message: 'Field is required' })
    .min(1, 'Field is required')
    .regex(BANK_NAME_REGEX, 'Invalid format'),
  address: bankAddressSchema,
});

//send

export const sendMoneyInfoSchema = z
  .object({
    account: z.custom<ICurrencyAccount>(),
    country: z.object({ id: z.string(), name: z.string() }).optional(), //TODO no in ui
    type: z.enum(VabsParticipantType),
    accType: z.enum(VabsAchAccountType).or(z.enum(VabsWireAccountType)).optional(),
    paymentSystem: z.enum(VabsPaymentSystem),
    accountNumber: z
      .string()
      .min(1, 'Field is required')
      .max(34, 'Account number must be at most 34 characters')
      .regex(BANK_ACCOUNT_NO_REGEX, 'Invalid format'),
    bankCode: z
      .string()
      .min(1, 'Field is required')
      .regex(BANK_CODE_REGEX, 'Invalid format'),
    firstName: z.string().regex(FIRST_NAME_REGEX, 'Invalid format'),
    lastName: z.string(),
    companyName: z.string().regex(COMPANY_NAME_REGEX, 'Invalid format'),
    intermediaryBank: z
      .object({
        accountNumber: z
          .string({ message: 'Field is required' })
          .min(1, 'Field is required')
          .regex(BANK_ACCOUNT_NO_REGEX, 'Invalid format'),
        swift: z
          .string({ message: 'Field is required' })
          .regex(BANK_CODE_REGEX, 'Invalid format')
          .length(8, 'SWIFT/BIC must be exactly 8 or 11 characters')
          .or(
            z
              .string({ message: 'Field is required' })
              .length(11, 'SWIFT/BIC must be exactly 8 or 11 characters'),
          ),
        bankInfo: bankInfoSchema,
      })
      .optional(),
  })
  .refine(
    (schema) =>
      schema.type === VabsParticipantType.B2B ? !!schema.companyName : true,
    {
      message: 'Field is required',
      path: ['companyName'],
    },
  )
  .refine(
    (schema) =>
      schema.paymentSystem === VabsPaymentSystem.Bsb
        ? schema.bankCode?.length >= 7
        : true,
    {
      message: 'Minimum 7 characters',
      path: ['bankCode'],
    },
  )
    .refine(
    (schema) =>
      (schema.paymentSystem === VabsPaymentSystem.Ach || schema.paymentSystem === VabsPaymentSystem.Wire)
        ? schema.bankCode?.length >= 9
        : true,
    {
      message: 'Minimum 9 characters',
      path: ['bankCode'],
    },
  )
   .refine(
    (schema) =>
      (schema.paymentSystem === VabsPaymentSystem.Ach || schema.paymentSystem === VabsPaymentSystem.Wire)
        ? !!schema.accType
        : true,
    {
      message: 'Field is required',
      path: ['accType'],
    },
  )
  .refine(
    (schema) =>
      schema.paymentSystem === VabsPaymentSystem.Swift
        ? isValidBankCode(schema.bankCode)
        : true,
    {
      message: 'SWIFT/BIC must be exactly 8 or 11 characters',
      path: ['bankCode'],
    },
  )
  .refine(
    (schema) =>
      schema.type === VabsParticipantType.B2C ? !!schema.firstName : true,
    {
      message: 'Field is required',
      path: ['firstName'],
    },
  )
  .refine(
    (schema) =>
      schema.type === VabsParticipantType.B2C ? !!schema.lastName : true,
    {
      message: 'Field is required',
      path: ['lastName'],
    },
  );

export const sendMoneyRecipientSchema = z.object({
  email: z.union([z.literal(''), z.email()], {
    message: 'Incorrect email, follow this format: example@example.com',
  }),
  recipientAddress: z.object({
    country: z.object(
      { id: z.string({ message: 'Field is required' }), name: z.string() },
      { message: 'Field is required' },
    ),
    street: z
      .string()
      .min(1, 'Field is required')
      .regex(BENEFICIARY_ADDRESS_REGEX, 'Invalid format'),
    postcode: z.string().min(1, 'Field is required'),
    city: z.string().min(1, 'Field is required'),
  }),
});

export const sendMoneyBankSchema = z.object({
  name: bankInfoSchema.shape.name,
  address: bankInfoSchema.shape.address,
});

export type SendMoneyInfoStepData = z.infer<typeof sendMoneyInfoSchema>;
export type SendMoneyRecipientStepData = z.infer<
  typeof sendMoneyRecipientSchema
>;
export type SendMoneyBankStepData = z.infer<typeof sendMoneyBankSchema>;

export type SendMoneyFormData = SendMoneyInfoStepData &
  SendMoneyRecipientStepData &
  SendMoneyBankStepData & {
    amount: string;
    totalAmount: string;
    fee: string;
    purposeOfPayment?: string;
    sourceOfFunds?: VabsPaymentSourceOfFunds;
    id?: string; //payment id for adding to favorites
    isFav?: boolean; // payment was favorite when repeated
    //for inner payments
    isInner?: boolean;
    clientId?: string;
    innerAccountId?: string;
    innerEmail?: string;
    innerPhone?: string;
    reference?: string;
    innerAvailableCurrencies?: string[];
  };
